import React from 'react'
import { Badge, Button, Card, CardContent, CardHeader } from '@/shared/ui'
import { useRewardsEnabled } from '../hooks/useRewardsEnabled'
import { useRewardsMasterDashboard } from '../hooks/useRewardsMasterDashboard'
import RewardsMasterExchangeTabs from '../components/master/RewardsMasterExchangeTabs'
import RewardsMasterKpiCards from '../components/master/RewardsMasterKpiCards'
import RewardsMasterPerformanceChartCard from '../components/master/RewardsMasterPerformanceChartCard'
import RewardsMasterUsersTable, { type RewardsMasterUserRow } from '../components/master/RewardsMasterUsersTable'
import { REWARDS_EXCHANGES, type RewardsExchange } from '../types'
import { calcDiscountBrlFromCommissionUsdt } from '../utils/rewardsMath'
import { formatCurrency, formatUsdt } from '../utils/rewardsFormat'
import {
  bucketEvents,
  getPeriodBuckets,
  getPeriodLabel,
  getPeriodRange,
  REWARDS_MASTER_PERIODS,
  sumEventsInRange,
  type RewardsMasterMetric,
  type RewardsMasterPeriod,
} from '../utils/rewardsMasterChart'

const METRIC_LABELS: Record<RewardsMasterMetric, string> = {
  links: 'Cadastros',
  volume: 'Volume',
  commission: 'Comissao',
  discount: 'Desconto',
}

export default function RewardsMasterPage() {
  const rewardsEnabled = useRewardsEnabled()
  const {
    loading,
    error,
    links,
    ownerByAccount,
    openMetricsByAccountExchange,
    historyCyclesById,
    historyMetrics,
    multiplierX,
    usdtBrlRate,
    reload,
  } = useRewardsMasterDashboard()

  const [exchange, setExchange] = React.useState<RewardsExchange>(REWARDS_EXCHANGES[0])
  const [period, setPeriod] = React.useState<RewardsMasterPeriod>('month')
  const [metric, setMetric] = React.useState<RewardsMasterMetric>('links')

  const exchangeLinks = React.useMemo(() => links.filter((l) => l.exchange === exchange), [links, exchange])

  const rows = React.useMemo<RewardsMasterUserRow[]>(() => {
    return exchangeLinks.map((l) => {
      const owner = (ownerByAccount as any).get(l.account_id) || null
      const metricRow = openMetricsByAccountExchange.get(`${l.account_id}:${l.exchange}`)
      const volumeUsdt = Number(metricRow?.volume_usdt || 0)
      const commissionConfirmed = Number(metricRow?.commission_confirmed_usdt || 0)
      const commissionUsdt = commissionConfirmed > 0 ? commissionConfirmed : Number(metricRow?.commission_estimated_usdt || 0)
      const discountBrl = calcDiscountBrlFromCommissionUsdt({
        commissionConfirmedUsdt: commissionConfirmed,
        multiplierX,
        usdtBrlRate,
      })
      return {
        id: l.id,
        accountId: l.account_id,
        exchangeUserId: l.exchange_user_id,
        status: l.status,
        linkedAt: l.linked_at,
        lastSyncAt: l.last_sync_at,
        createdAt: l.created_at,
        updatedAt: l.updated_at,
        ownerName: owner?.name || null,
        ownerEmail: owner?.email || null,
        volumeUsdt,
        commissionUsdt,
        discountBrl,
      }
    })
  }, [exchangeLinks, ownerByAccount, openMetricsByAccountExchange, multiplierX, usdtBrlRate])

  const linkEvents = React.useMemo(() => {
    return exchangeLinks
      .filter((l) => l.status === 'linked')
      .map((l) => {
        const stamp = resolveLinkedAt(l)
        return stamp ? { timeMs: stamp, value: 1 } : null
      })
      .filter(Boolean) as Array<{ timeMs: number; value: number }>
  }, [exchangeLinks])

  const metricEvents = React.useMemo(() => {
    const volume: Array<{ timeMs: number; value: number }> = []
    const commission: Array<{ timeMs: number; value: number }> = []
    const discount: Array<{ timeMs: number; value: number }> = []

    for (const m of historyMetrics) {
      if (m.exchange !== exchange) continue
      const cycle = historyCyclesById.get(m.cycle_id)
      if (!cycle) continue
      const timeMs = Date.parse(cycle.period_start)
      if (!Number.isFinite(timeMs)) continue
      const volumeValue = Number(m.volume_usdt || 0)
      const commissionValue = Number(m.commission_confirmed_usdt || m.commission_estimated_usdt || 0)
      const discountValue = calcDiscountBrlFromCommissionUsdt({
        commissionConfirmedUsdt: Number(m.commission_confirmed_usdt || 0),
        multiplierX,
        usdtBrlRate,
      })
      volume.push({ timeMs, value: volumeValue })
      commission.push({ timeMs, value: commissionValue })
      discount.push({ timeMs, value: discountValue })
    }

    return { volume, commission, discount }
  }, [historyMetrics, historyCyclesById, exchange, multiplierX, usdtBrlRate])

  const buckets = React.useMemo(() => getPeriodBuckets(period), [period])
  const range = React.useMemo(() => getPeriodRange(buckets), [buckets])

  const totals = React.useMemo(() => {
    if (!range) return { links: 0, volume: 0, commission: 0, discount: 0 }
    return {
      links: sumEventsInRange(linkEvents, range.startMs, range.endMs),
      volume: sumEventsInRange(metricEvents.volume, range.startMs, range.endMs),
      commission: sumEventsInRange(metricEvents.commission, range.startMs, range.endMs),
      discount: sumEventsInRange(metricEvents.discount, range.startMs, range.endMs),
    }
  }, [range, linkEvents, metricEvents])

  const selectedPoints = React.useMemo(() => {
    if (metric === 'links') return bucketEvents(linkEvents, buckets)
    if (metric === 'volume') return bucketEvents(metricEvents.volume, buckets)
    if (metric === 'commission') return bucketEvents(metricEvents.commission, buckets)
    return bucketEvents(metricEvents.discount, buckets)
  }, [metric, linkEvents, metricEvents, buckets])

  if (rewardsEnabled === null) return <div className="p-6 text-sm text-foreground/70">Carregando...</div>
  if (rewardsEnabled === false) {
    return (
      <div className="mx-auto w-full max-w-[1200px] p-6">
        <Card>
          <CardHeader className="text-base font-semibold">Rewards (Master)</CardHeader>
          <CardContent className="text-sm text-foreground/70">
            O modulo Rewards nao esta habilitado para sua conta. Em caso de duvida, ajuste as politicas no painel MASTER.
          </CardContent>
        </Card>
      </div>
    )
  }

  const discountEnabled = usdtBrlRate > 0
  const cards = [
    {
      metric: 'links' as const,
      label: 'Usuarios vinculados',
      value: totals.links.toLocaleString('pt-BR'),
      tone: 'primary' as const,
    },
    {
      metric: 'volume' as const,
      label: 'Volume gerado',
      value: `${formatUsdt(totals.volume)} USDT`,
      tone: 'success' as const,
    },
    {
      metric: 'commission' as const,
      label: 'Comissao gerada',
      value: `${formatUsdt(totals.commission)} USDT`,
      tone: 'warning' as const,
    },
    {
      metric: 'discount' as const,
      label: 'Desconto gerado',
      value: discountEnabled ? formatCurrency(totals.discount, 'BRL') : '-',
      tone: 'violet' as const,
    },
  ]

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Rewards - Master</h1>
          <p className="text-sm text-foreground/70">
            Gestao dos vinculados por corretora, com performance agregada por periodo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reload} disabled={loading}>
            Recarregar
          </Button>
          {loading && (
            <Badge variant="soft" color="info">
              Carregando
            </Badge>
          )}
        </div>
      </div>

      <RewardsMasterExchangeTabs value={exchange} onChange={setExchange} />

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-9">
          <RewardsMasterPerformanceChartCard
            metric={metric}
            metricLabel={METRIC_LABELS[metric]}
            period={period}
            periodOptions={REWARDS_MASTER_PERIODS}
            onPeriodChange={setPeriod}
            points={selectedPoints}
            totalLabel={formatMetricValue(metric, totals[metric], discountEnabled)}
            loading={loading}
            error={error}
            note={discountEnabled ? null : metric === 'discount' ? 'Taxa USDT->BRL nao sincronizada.' : null}
          />
        </div>
        <div className="lg:col-span-3">
          <RewardsMasterKpiCards items={cards} activeMetric={metric} onSelect={setMetric} />
          <div className="mt-3 text-xs text-foreground/60">Periodo: {getPeriodLabel(period)}</div>
        </div>
      </div>

      <RewardsMasterUsersTable
        rows={rows}
        loading={loading}
        error={error}
        onReload={reload}
        discountEnabled={discountEnabled}
      />
    </div>
  )
}

function resolveLinkedAt(link: { linked_at?: string | null; status?: string; updated_at?: string; created_at?: string }): number | null {
  const linkedAt = link?.linked_at ? Date.parse(link.linked_at) : NaN
  if (Number.isFinite(linkedAt)) return linkedAt
  if (link?.status !== 'linked') return null
  const fallback = link?.updated_at || link?.created_at
  const ms = fallback ? Date.parse(fallback) : NaN
  return Number.isFinite(ms) ? ms : null
}

function formatMetricValue(metric: RewardsMasterMetric, value: number, discountEnabled: boolean): string {
  if (metric === 'links') return Number(value || 0).toLocaleString('pt-BR')
  if (metric === 'discount') return discountEnabled ? formatCurrency(value, 'BRL') : '-'
  return `${formatUsdt(value)} USDT`
}
